import { Card, CardContent } from '../ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Badge } from '../ui/badge';
import { COMPANY } from '../../data/recruitmentContent';
import { Award, ShieldCheck, Leaf, CheckCircle2, Factory, Wrench, Building2, Star } from 'lucide-react';

const BulletList = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
        <CheckCircle2 size={15} className="text-[#1a4b8c] shrink-0 mt-0.5" /> <span>{it}</span>
      </li>
    ))}
  </ul>
);

export const CompanyInfo = () => {
  return (
    <div className="space-y-6" data-testid="company-info">
      {/* Chi siamo */}
      <Card className="border-slate-200">
        <CardContent className="p-5 md:p-6 space-y-3">
          <div className="flex items-center gap-2">
            <Building2 size={20} className="text-[#1a4b8c]" />
            <h2 className="text-xl font-bold text-slate-800">{COMPANY.chiSiamo.title}</h2>
          </div>
          {COMPANY.chiSiamo.paragraphs.map((p, i) => (
            <p key={i} className="text-sm text-slate-600 leading-relaxed">{p}</p>
          ))}
        </CardContent>
      </Card>

      {/* Certificazioni */}
      <div data-testid="certifications">
        <div className="flex items-center gap-2 mb-3">
          <Award size={20} className="text-[#1a4b8c]" />
          <h2 className="text-xl font-bold text-slate-800">Certificazioni</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          {COMPANY.certificazioni.map((c) => (
            <div key={c.code} className="rounded-xl border-2 border-[#1a4b8c]/20 bg-gradient-to-br from-blue-50 to-white p-4 text-center" data-testid={`cert-badge-${c.code}`}>
              <ShieldCheck size={26} className="text-[#1a4b8c] mx-auto mb-2" />
              <p className="font-bold text-slate-800 text-sm">{c.code}</p>
              <p className="text-xs text-slate-500 mt-1">{c.name}</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">{COMPANY.certificazioniNota}</p>
      </div>

      {/* Cosa facciamo - griglia */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Wrench size={20} className="text-[#1a4b8c]" />
          <h2 className="text-xl font-bold text-slate-800">Cosa facciamo</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {COMPANY.cosaFacciamo.map((s, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-4 space-y-2">
                <h3 className="font-semibold text-slate-800 text-sm">{s.title}</h3>
                <BulletList items={s.items} />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Perché sceglierci */}
      <Card className="border-slate-200 bg-slate-50">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Star size={20} className="text-[#1a4b8c]" />
            <h2 className="text-xl font-bold text-slate-800">Perché scegliere Metal Covering</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {COMPANY.perche.map((p, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle2 size={15} className="text-[#1a4b8c] shrink-0 mt-0.5" /> <span>{p}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dettagli in accordion */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Factory size={20} className="text-[#1a4b8c]" />
          <h2 className="text-xl font-bold text-slate-800">Qualità, sicurezza e ambiente</h2>
        </div>
        <Accordion type="single" collapsible className="rounded-xl border border-slate-200 bg-white px-4">
          <AccordionItem value="qualita">
            <AccordionTrigger className="text-sm font-semibold">Controllo qualità</AccordionTrigger>
            <AccordionContent className="space-y-2">
              <p className="text-sm text-slate-600">{COMPANY.controlloQualita.intro}</p>
              <BulletList items={COMPANY.controlloQualita.items} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="sicurezza">
            <AccordionTrigger className="text-sm font-semibold">Sicurezza sul lavoro</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-sm text-slate-600">{COMPANY.sicurezza.intro}</p>
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Durante le lavorazioni</p>
                <BulletList items={COMPANY.sicurezza.durante} />
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Documentazione di sicurezza</p>
                <BulletList items={COMPANY.sicurezza.documenti} />
              </div>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="confinati">
            <AccordionTrigger className="text-sm font-semibold">Lavori in ambienti confinati</AccordionTrigger>
            <AccordionContent className="space-y-2">
              <p className="text-sm text-slate-600">{COMPANY.ambientiConfinati.intro}</p>
              <BulletList items={COMPANY.ambientiConfinati.items} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="ambiente">
            <AccordionTrigger className="text-sm font-semibold">Tutela dell'ambiente</AccordionTrigger>
            <AccordionContent className="space-y-2">
              <p className="text-sm text-slate-600">{COMPANY.ambiente.intro}</p>
              <BulletList items={COMPANY.ambiente.items} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="clienti">
            <AccordionTrigger className="text-sm font-semibold">Esperienze e clienti</AccordionTrigger>
            <AccordionContent className="space-y-3">
              <p className="text-sm text-slate-600">{COMPANY.clienti.formula}</p>
              <div className="flex flex-wrap gap-2">
                {COMPANY.clienti.names.map((n) => (
                  <Badge key={n} variant="outline" className="border-[#1a4b8c]/30 text-slate-600">{n}</Badge>
                ))}
              </div>
              <BulletList items={COMPANY.clienti.esperienze} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="metodo">
            <AccordionTrigger className="text-sm font-semibold">Il nostro metodo di lavoro</AccordionTrigger>
            <AccordionContent>
              <ol className="space-y-1.5 list-decimal list-inside">
                {COMPANY.metodo.map((m, i) => (
                  <li key={i} className="text-sm text-slate-600">{m}</li>
                ))}
              </ol>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  );
};
